// src/components/Timerapp.js

import React, { useState, useEffect } from 'react';
import './TimerApp.css';
import { useSettings } from '../../hooks/useSettings';
import { useTasks } from '../../hooks/useTasks';
import InstructionModal from './InstructionModal';
import ModeSelector from './ModeSelector';
import TimerDisplay from './TimerDisplay';
import ProgressBar from './ProgressBar';
import Celebration from './Celebration';
import SoundPlayer from './SoundPlayer';
import CompletionPlayer from './CompletionPlayer';
import TaskInput from './TaskInput';
import TaskList from './TaskList';
import SessionLog from './SessionLog';
import SettingsModal from './SettingsModal';
import MusicModal from './MusicModal';

const DEFAULT_TRACKS = [
  { name: 'Follow God', url: 'https://www.youtube.com/watch?v=ivCY3Ec4iaU' }
];
const TRACKS_LOCAL_STORAGE_KEY = 'pomodoro_tracks';

function TimerApp() {
  const [settings, setSettings] = useSettings();
  const [sessionLogs, setSessionLogs] = useState([]);
  const [tracks, setTracks] = useState(DEFAULT_TRACKS);
  const [musicModalOpen, setMusicModalOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [playCompletionSound, setPlayCompletionSound] = useState(false);
  const [stopBgMusic, setStopBgMusic] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const {
    tasks,
    newTask,
    estimatedPomodoros,
    setNewTask,
    setEstimatedPomodoros,
    addTask,
    toggleTaskCompletion,
    setTasks,
  } = useTasks();

  const [mode, setMode] = useState('Pomodoro');
  const [timeLeft, setTimeLeft] = useState(settings.pomodoroDuration);
  const [isActive, setIsActive] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [cycleCount, setCycleCount] = useState(0);

  function addLog(msg) {
    setSessionLogs((prev) => [
      ...prev,
      {
        message: msg,
        time: Date.now(),
      },
    ]);
  }

  // ⏱ Set time based on mode/settings
  useEffect(() => {
    const duration =
      mode === 'Pomodoro'
        ? settings.pomodoroDuration
        : mode === 'Short Break'
        ? settings.shortBreakDuration
        : settings.longBreakDuration;

    setTimeLeft(duration);
    setIsActive(false);
  }, [mode, settings]);

  // ✅ Track used Pomodoros for the first active task
  const trackCompletedPomodoro = () => {
    setTasks((prevTasks) => {
      const updatedTasks = [...prevTasks];
      const targetIndex = updatedTasks.findIndex(task => !task.completed);
  
      if (targetIndex !== -1) {
        const task = updatedTasks[targetIndex];
        const used = (task.usedPomodoros || 0) + 1;
  
        updatedTasks[targetIndex] = {
          ...task,
          usedPomodoros: used,
          completed: used >= task.estimatedPomodoros,
        };
      }
  
      return updatedTasks;
    });
  };

  // ⏳ Countdown
  useEffect(() => {
    let interval = null;
    if (isActive) {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setShowCelebration(true);
            setPlayCompletionSound(true);
            setIsActive(false);
            addLog(`${mode} complete`);

            if (mode === 'Pomodoro') {
              trackCompletedPomodoro();
              setCycleCount((prev) => (prev % 4) + 1);
              setMode('Short Break');
            } else if (mode === 'Short Break' || mode === 'Long Break') {
              setMode('Pomodoro');
            }

            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isActive, mode]);

  const resetTimer = () => {
    const duration =
      mode === 'Pomodoro'
        ? settings.pomodoroDuration
        : mode === 'Short Break'
        ? settings.shortBreakDuration
        : settings.longBreakDuration;

    setTimeLeft(duration);
    setIsActive(false);
  };

  const stopAllAudio = () => {
    setPlayCompletionSound(false);
    setStopBgMusic(true);
  };

  const endCelebration = () => setShowCelebration(false);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  // Title
  useEffect(() => {
    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');
    document.title = isActive ? `${mm}:${ss} - ${mode}` : 'ProdPilot - Timer';
  }, [minutes, seconds, isActive, mode]);

  // Theme + Notification
  useEffect(() => {
    document.body.className = settings.theme;
  }, [settings.theme]);

  useEffect(() => {
    if (settings.notificationEnabled && Notification.permission !== 'granted') {
      Notification.requestPermission();
    }
  }, [settings.notificationEnabled]);

  // Load/save music
  useEffect(() => {
    const saved = localStorage.getItem(TRACKS_LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.every(t => t.name && t.url)) {
          setTracks(parsed);
        }
      } catch (err) {
        console.error('Error parsing track list:', err);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(TRACKS_LOCAL_STORAGE_KEY, JSON.stringify(tracks));
  }, [tracks]);

  return (
    <div className={`TimerApp ${settings.theme}`}>
      {/* TOP ROW */}
      <div className="row top-row">
        <div className="col left-col">
          <div className="timer-container card">
                        <div className="mode-indicator">
              <span>
                {mode}
                {mode === 'Pomodoro' && cycleCount > 0
                  ? ` - Cycle ${cycleCount} of 4`
                  : ''}
              </span>
            </div>
            <ModeSelector
              mode={mode}
              setMode={setMode}
              setTimeLeft={setTimeLeft}
              setIsActive={setIsActive}
              settings={settings}
            />
            <TimerDisplay minutes={minutes} seconds={seconds} />
            <ProgressBar
              timeLeft={timeLeft}
              duration={
                mode === 'Pomodoro'
                  ? settings.pomodoroDuration
                  : mode === 'Short Break'
                  ? settings.shortBreakDuration
                  : settings.longBreakDuration
              }
            />
<div className="timer-buttons">
  <button onClick={() => { stopAllAudio(); setIsActive(!isActive); }}>
    {isActive ? 'Pause' : 'Start'}
  </button>
  <button onClick={() => { stopAllAudio(); resetTimer(); }}>
    Reset
  </button>
  <button onClick={() => setIsSettingsOpen(true)}>Settings</button>
  <button onClick={() => setShowInstructions(true)}>Help</button>
</div>
{/* INSTRUCTION MODAL */}
{showInstructions && (
  <InstructionModal onClose={() => setShowInstructions(false)} />
)}

            {showCelebration && (
              <Celebration endCelebration={endCelebration} />
            )}
          </div>
        </div>

        <div className="col right-col timer-buttons">
          <div className="youtube-container card">
            <h3>Pulse</h3>
            <div className="sound-controls">
              {settings.soundEnabled && (
                <SoundPlayer
                  youtubeTracks={tracks}
                  stopPlayback={stopBgMusic}
                  onStopped={() => setStopBgMusic(false)}
                />
              )}
              <button onClick={() => setMusicModalOpen(true)}>Manage Videos</button>
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM ROW */}
      <div className="row bottom-row">
        <div className="col half-col">
          <div className="task-input-container card">
            <h3>Task</h3>
            <TaskInput
              newTask={newTask}
              setNewTask={setNewTask}
              estimatedPomodoros={estimatedPomodoros}
              setEstimatedPomodoros={setEstimatedPomodoros}
              addTask={addTask}
            />
            <TaskList
              tasks={tasks}
              toggleTaskCompletion={toggleTaskCompletion}
            />
          </div>
        </div>
        <div className="col half-col">
          <div className="session-log card">
            <h3>Session</h3>
            <SessionLog sessionLogs={sessionLogs} />
          </div>
        </div>
      </div>

      {/* SETTINGS MODAL */}
      {isSettingsOpen && (
        <SettingsModal
          settings={settings}
          setSettings={setSettings}
          onClose={() => setIsSettingsOpen(false)}
        />
      )}

      {/* MUSIC MODAL */}
      {musicModalOpen && (
        <MusicModal
          tracks={tracks}
          setTracks={setTracks}
          defaultTracks={DEFAULT_TRACKS}
          setCompletionUrl={(url) =>
            setSettings((prev) => ({ ...prev, notificationSound: url }))
          }
          onClose={() => setMusicModalOpen(false)}
        />
      )}

      {/* COMPLETION SOUND */}
      {playCompletionSound && (
        <CompletionPlayer
          playUrl={
            settings.notificationSound
              ? settings.notificationSound
              : '/audio/default-beep.mp3'
          }
          onEnd={() => setPlayCompletionSound(false)}
        />
      )}
    </div>
  );
}

export default TimerApp;
